<template>
  <div class="bg p-3 mb-auto h-full overflow-auto bg-gradient-to-b bg-gray-800" id="style-1">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4 ">
      <h1 class="text-white text-xl sm:text-2xl font-semibold flex items-center gap-2">
        <i class="fas fa-biohazard text-indigo-300"></i>
        ตั้งค่า Raid Boss
      </h1>
      <div class="text-sm text-indigo-200 flex items-center gap-4">
        <span>
          Event ID: <span class="font-mono px-2 py-1 rounded bg-gray-700 border border-gray-600">{{ eventId || '-' }}</span>
        </span>
        <div class="flex items-center gap-2">
          <span class="text-gray-300">เปิดให้เข้าร่วม:</span>
          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" v-model="join" class="sr-only peer" />
            <div class="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-indigo-400 rounded-full peer transition-colors duration-200 ease-in-out peer-checked:bg-indigo-600"></div>
            <div class="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-all duration-200 ease-in-out peer-checked:translate-x-5"></div>
          </label>
          <span :class="[join ? 'text-green-300' : 'text-red-300']">{{ join ? 'ใช่' : 'ไม่ใช่' }}</span>
        </div>
      </div>
    </div>

    <!-- Alerts -->
    <transition name="fade">
      <div
        v-if="banner.message"
        :class="['mb-4 rounded-md border px-3 py-2 text-sm',
                 banner.type === 'success' ? 'bg-green-900 bg-opacity-30 border-green-700 text-green-200' :
                 banner.type === 'error' ? 'bg-red-900 bg-opacity-30 border-red-700 text-red-200' :
                 'bg-indigo-900 bg-opacity-30 border-indigo-700 text-indigo-200']">
        <div class="flex items-center gap-2">
          <i :class="bannerIcon"></i>
          <span>{{ banner.message }}</span>
          <button class="ml-auto text-gray-300 hover:text-white" @click="clearBanner">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </transition>

    <!-- Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Left Column -->
      <div class="space-y-4">
        <!-- Basic Settings -->
        <div class="bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md p-4">
          <div class="flex items-center gap-2 mb-3">
            <i class="fas fa-sliders-h text-indigo-300"></i>
            <h2 class="text-white text-lg font-medium">ค่าพื้นฐาน</h2>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-gray-300 text-sm mb-1">จำนวนซอมบี้ขั้นต่ำ (Min Zombies)</label>
              <input
                v-model.number="minZombies"
                type="number"
                min="1"
                class="w-full bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <p v-if="errors.min" class="mt-1 text-xs text-red-300">{{ errors.min }}</p>
            </div>

            <div>
              <label class="block text-gray-300 text-sm mb-1">จำนวนซอมบี้ขั้นสูงสุด (Max Zombies)</label>
              <input
                v-model.number="maxZombies"
                type="number"
                min="1"
                class="w-full bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <p v-if="errors.max" class="mt-1 text-xs text-red-300">{{ errors.max }}</p>
            </div>
          </div>

          <!-- Final Bosses (multiple, duplicates allowed) -->
          <div class="mt-4">
            <label class="block text-gray-300 text-sm mb-1">บอสตัวสุดท้าย (Final Bosses)</label>
            <div class="flex gap-2 items-center">
              <input
                v-model.trim="newFinalBoss"
                placeholder="เพิ่มชื่อบอส และกด Enter (อนุญาตชื่อซ้ำได้)"
                type="text"
                class="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                @keydown.enter.prevent="addFinalBoss"
              />
              <button
                class="px-3 py-2 rounded-md bg-green-600 hover:bg-green-500 text-white border border-green-500 transition"
                @click="addFinalBoss"
                :disabled="!newFinalBoss"
                title="เพิ่มบอส"
              >
                <i class="fas fa-plus"></i>
              </button>
              <button
                class="px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-gray-200 hover:text-white hover:border-indigo-400 transition"
                @click="clearFinalBosses"
                :disabled="finalBosses.length === 0"
                title="ล้างทั้งหมด"
              >
                <i class="fas fa-eraser"></i>
              </button>
            </div>
            <p v-if="errors.finalBoss" class="mt-1 text-xs text-red-300">{{ errors.finalBoss }}</p>

            <div class="mt-3 flex flex-wrap gap-2">
              <span
                v-for="(boss, index) in finalBosses"
                :key="`fb-${index}-${boss}`"
                class="inline-flex items-center gap-2 bg-gray-700 border border-gray-600 text-indigo-200 rounded-full px-3 py-1 text-sm"
              >
                <i class="fas fa-crown"></i> {{ boss }}
                <button
                  class="ml-1 text-gray-300 hover:text-red-300"
                  @click="removeFinalBoss(index)"
                  title="ลบ"
                >
                  <i class="fas fa-times"></i>
                </button>
              </span>
            </div>
            <p class="mt-1 text-xxs text-gray-400">ทั้งหมด: {{ finalBosses.length }}</p>
          </div>
        </div>

        <!-- Zombie List -->
        <div class="bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-skull-crossbones text-indigo-300"></i>
              <h2 class="text-white text-lg font-medium">รายชื่อซอมบี้ที่จะถูกสุ่มเจอ</h2>
            </div>
            <span class="text-xs text-gray-400">ทั้งหมด: {{ zombieList.length }}</span>
          </div>

          <div class="flex gap-2 mb-3">
            <input
              v-model.trim="newZombie"
              placeholder="เพิ่มชื่อซอมบี้ และกด Enter"
              class="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              @keydown.enter.prevent="addZombie"
            />
            <button @click="addZombie" class="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-md transition">
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <p v-if="errors.zombie" class="mb-2 text-xs text-red-300">{{ errors.zombie }}</p>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(zombie, index) in zombieList"
              :key="`z-${index}`"
              class="inline-flex items-center gap-2 bg-gray-700 border border-gray-600 text-indigo-200 rounded-full px-3 py-1 text-sm"
            >
              <i class="fas fa-virus"></i> {{ zombie }}
              <button
                class="ml-1 text-gray-300 hover:text-red-300"
                @click="removeZombie(index)"
                title="ลบ"
              >
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="space-y-4">
        <!-- Join Rewards -->
        <div class="bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-ticket-alt text-indigo-300"></i>
              <h2 class="text-white text-lg font-medium">รางวัลเข้าร่วม (Join Rewards)</h2>
            </div>
            <span class="text-xs text-gray-400">ทั้งหมด: {{ joinRewards.length }}</span>
          </div>

          <div class="flex gap-2 mb-3">
            <input
              v-model="newJoinReward"
              placeholder="เพิ่มชื่อไอเทม และกด Enter"
              class="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              @keydown.enter.prevent="addJoinReward"
            />
            <button @click="addJoinReward" class="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-md transition">
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(it, index) in joinRewards"
              :key="'join-r-'+index"
              class="inline-flex items-center gap-2 bg-gray-700 border border-gray-600 text-indigo-200 rounded-full px-3 py-1 text-sm"
            >
              <i class="fas fa-gift"></i> {{ it }}
              <button class="ml-1 text-gray-300 hover:text-red-300" @click="removeJoinReward(index)" title="ลบ">
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>

        <!-- Last Kill Rewards -->
        <div class="bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-crown text-indigo-300"></i>
              <h2 class="text-white text-lg font-medium">รางวัลผู้สังหารบอสคนสุดท้าย (Last Kill)</h2>
            </div>
            <span class="text-xs text-gray-400">ทั้งหมด: {{ lastKillRewards.length }}</span>
          </div>

          <div class="flex gap-2 mb-3">
            <input
              v-model="newLastKillReward"
              placeholder="เพิ่มชื่อไอเทม และกด Enter"
              class="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              @keydown.enter.prevent="addLastKillReward"
            />
            <button @click="addLastKillReward" class="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-md transition">
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(it, index) in lastKillRewards"
              :key="'lastkill-r-'+index"
              class="inline-flex items-center gap-2 bg-gray-700 border border-gray-600 text-indigo-200 rounded-full px-3 py-1 text-sm"
            >
              <i class="fas fa-star"></i> {{ it }}
              <button class="ml-1 text-gray-300 hover:text-red-300" @click="removeLastKillReward(index)" title="ลบ">
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>

        <!-- Top Damage Rewards -->
        <div class="bg-gray-800 bg-opacity-80 border border-gray-700 rounded-md p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-fire-alt text-indigo-300"></i>
              <h2 class="text-white text-lg font-medium">รางวัลผู้ทำดาเมจสูงสุด (Top Damage)</h2>
            </div>
            <span class="text-xs text-gray-400">ทั้งหมด: {{ topDamageRewards.length }}</span>
          </div>

          <div class="flex gap-2 mb-3">
            <input
              v-model="newTopDamageReward"
              placeholder="เพิ่มชื่อไอเทม และกด Enter"
              class="flex-1 bg-gray-700 text-white px-3 py-2 rounded-md border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              @keydown.enter.prevent="addTopDamageReward"
            />
            <button @click="addTopDamageReward" class="bg-green-600 hover:bg-green-500 text-white px-3 py-2 rounded-md transition">
              <i class="fas fa-plus"></i>
            </button>
          </div>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="(it, index) in topDamageRewards"
              :key="'topdamage-r-'+index"
              class="inline-flex items-center gap-2 bg-gray-700 border border-gray-600 text-indigo-200 rounded-full px-3 py-1 text-sm"
            >
              <i class="fas fa-bolt"></i> {{ it }}
              <button class="ml-1 text-gray-300 hover:text-red-300" @click="removeTopDamageReward(index)" title="ลบ">
                <i class="fas fa-times"></i>
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
      <button
        class="w-full sm:w-auto bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded-md border border-gray-600 transition"
        @click="reload"
        :disabled="loading"
      >
        <i class="fas fa-sync-alt mr-2" :class="loading ? 'animate-spin' : ''"></i> โหลดจากเซิร์ฟเวอร์
      </button>

      <div class="ml-auto flex items-center gap-2">
        <button
          class="w-full sm:w-auto bg-indigo-700 hover:bg-indigo-600 text-white px-4 py-2 rounded-md border border-indigo-600 transition disabled:opacity-50"
          :disabled="!isValid || saving || loading"
          @click="openConfirm = true"
        >
          <i class="fas fa-save mr-2"></i> บันทึกการตั้งค่า
        </button>
        <span v-if="saving" class="text-sm text-gray-300 flex items-center gap-2">
          <i class="fas fa-spinner animate-spin"></i> กำลังบันทึก...
        </span>
      </div>
    </div>

    <!-- Confirm Modal -->
    <transition name="fade">
      <div
        v-if="openConfirm"
        class="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
      >
        <div class="w-full max-w-md bg-gray-900 text-gray-200 rounded-lg p-5 border border-gray-700 animate-scaleIn">
          <div class="flex items-center gap-3 mb-3">
            <i class="fas fa-exclamation-triangle text-yellow-400"></i>
            <h3 class="text-lg font-semibold">ยืนยันการบันทึก</h3>
          </div>
          <p class="text-sm text-gray-300 mb-4">
            ต้องการบันทึกการตั้งค่า Raid Boss นี้หรือไม่?
            (Min: {{ minZombies }} / Max: {{ maxZombies }} / Final: {{ finalBosses.length ? finalBosses.join(', ') : '-' }} / Join: {{ join ? 'ใช่' : 'ไม่ใช่' }})
          </p>
          <div class="flex justify-end gap-2">
            <button
              class="px-4 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition"
              @click="openConfirm = false"
            >
              ยกเลิก
            </button>
            <button
              class="px-4 py-2 rounded-md bg-indigo-700 border border-indigo-600 text-white hover:bg-indigo-600 transition disabled:opacity-50"
              :disabled="saving"
              @click="save"
            >
              ยืนยันบันทึก
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config'
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'RaidBossSettings',
  setup() {
    const minZombies = ref(1)
    const maxZombies = ref(10)
    const zombieList = ref([])

    // finalBosses: many, duplicates allowed
    const finalBosses = ref([])
    const newFinalBoss = ref('')

    const joinRewards = ref([])
    const lastKillRewards = ref([])
    const topDamageRewards = ref([])
    const eventId = ref('')
    const join = ref(false)

    const newZombie = ref('')
    const newJoinReward = ref('')
    const newLastKillReward = ref('')
    const newTopDamageReward = ref('')

    const loading = ref(false)
    const saving = ref(false)
    const openConfirm = ref(false)

    const banner = ref({ type: '', message: '' })
    const errors = ref({ min: '', max: '', finalBoss: '', zombie: '', join: '', lastKill: '', topDamage: '' })

    const docRef = projectFirestore.collection('server').doc('raidboss')

    const clearBanner = () => (banner.value = { type: '', message: '' })
    const showBanner = (type, message) => (banner.value = { type, message })

    const bannerIcon = computed(() => {
      if (banner.value.type === 'success') return 'fas fa-check-circle'
      if (banner.value.type === 'error') return 'fas fa-times-circle'
      return 'fas fa-info-circle'
    })

    const validate = () => {
      errors.value = { min: '', max: '', finalBoss: '', zombie: '', join: '', lastKill: '', topDamage: '' }
      if (!minZombies.value || minZombies.value < 1) {
        errors.value.min = 'Min ต้องเป็นตัวเลขตั้งแต่ 1 ขึ้นไป'
      }
      if (!maxZombies.value || maxZombies.value < 1) {
        errors.value.max = 'Max ต้องเป็นตัวเลขตั้งแต่ 1 ขึ้นไป'
      }
      if (!errors.value.min && !errors.value.max && minZombies.value > maxZombies.value) {
        errors.value.max = 'Max ต้องมากกว่าหรือเท่ากับ Min'
      }
      // ไม่บังคับ validation สำหรับ finalBosses (อนุญาตว่าง/ซ้ำได้)
      return Object.values(errors.value).every(v => !v)
    }

    const isValid = computed(() => validate())

    const addUnique = (arrRef, value, errKey) => {
      const v = (value || '').trim()
      if (!v) return
      if (arrRef.value.includes(v)) {
        errors.value[errKey] = 'มีชื่อนี้อยู่แล้ว'
        return
      }
      arrRef.value.push(v)
    }

    const addDuplicateAllowed = (arrRef, value) => {
      const v = (value || '').trim()
      if (!v) return
      arrRef.value.push(v)
    }

    // CRUD: Final Bosses (duplicates allowed)
    const addFinalBoss = () => {
      addDuplicateAllowed(finalBosses, newFinalBoss.value)
      newFinalBoss.value = ''
    }
    const removeFinalBoss = (index) => finalBosses.value.splice(index, 1)
    const clearFinalBosses = () => (finalBosses.value = [])

    // CRUD: Zombies
    const addZombie = () => {
      addUnique(zombieList, newZombie.value, 'zombie')
      if (!errors.value.zombie) newZombie.value = ''
    }
    const removeZombie = (index) => {
      zombieList.value.splice(index, 1)
    }

    // CRUD: Rewards
    const addJoinReward = () => {
      addDuplicateAllowed(joinRewards, newJoinReward.value)
      newJoinReward.value = ''
    }
    const removeJoinReward = (index) => joinRewards.value.splice(index, 1)

    const addLastKillReward = () => {
      addDuplicateAllowed(lastKillRewards, newLastKillReward.value)
      newLastKillReward.value = ''
    }
    const removeLastKillReward = (index) => lastKillRewards.value.splice(index, 1)

    const addTopDamageReward = () => {
      addDuplicateAllowed(topDamageRewards, newTopDamageReward.value)
      newTopDamageReward.value = ''
    }
    const removeTopDamageReward = (index) => topDamageRewards.value.splice(index, 1)

    // Load
    const load = async () => {
      loading.value = true
      try {
        const doc = await docRef.get()
        if (doc.exists) {
          const data = doc.data() || {}
          minZombies.value = data.minZombies ?? 1
          maxZombies.value = data.maxZombies ?? 10
          zombieList.value = Array.isArray(data.zombieList) ? data.zombieList : []
          // รับเป็น array; ถ้าเป็น string เก่า แปลงเป็น array
          finalBosses.value = Array.isArray(data.finalBoss)
            ? data.finalBoss
            : (data.finalBoss ? [data.finalBoss] : [])
          joinRewards.value = Array.isArray(data.joinRewards) ? data.joinRewards : []
          lastKillRewards.value = Array.isArray(data.lastKillRewards) ? data.lastKillRewards : []
          topDamageRewards.value = Array.isArray(data.topDamageRewards) ? data.topDamageRewards : []
          eventId.value = data.event || ''
          join.value = data.join ?? false
        }
        clearBanner()
      } catch (e) {
        console.error(e)
        showBanner('error', 'โหลดข้อมูลล้มเหลว')
      } finally {
        loading.value = false
      }
    }

    const reload = () => load()

    onMounted(load)

    // Save (two-step)
    const save = async () => {
      if (!validate()) {
        showBanner('error', 'กรุณาแก้ไขข้อผิดพลาดก่อนบันทึก')
        openConfirm.value = false
        return
      }
      saving.value = true
      try {
        await docRef.set(
          {
            minZombies: Number(minZombies.value),
            maxZombies: Number(maxZombies.value),
            zombieList: zombieList.value,
            // บันทึกเป็นอาเรย์ (อนุญาตซ้ำ)
            finalBoss: finalBosses.value,
            joinRewards: joinRewards.value,
            lastKillRewards: lastKillRewards.value,
            topDamageRewards: topDamageRewards.value,
            join: join.value
          },
          { merge: true }
        )
        showBanner('success', 'บันทึกสำเร็จ')
        openConfirm.value = false
      } catch (e) {
        console.error(e)
        showBanner('error', 'บันทึกล้มเหลว')
      } finally {
        saving.value = false
      }
    }

    return {
      // state
      minZombies, maxZombies, zombieList,
      finalBosses, newFinalBoss,
      joinRewards, lastKillRewards, topDamageRewards,
      eventId, join,
      newZombie, newJoinReward, newLastKillReward, newTopDamageReward,
      loading, saving, openConfirm,
      banner, errors,

      // computed
      isValid, bannerIcon,

      // methods
      addFinalBoss, removeFinalBoss, clearFinalBosses,
      addZombie, removeZombie,
      addJoinReward, removeJoinReward,
      addLastKillReward, removeLastKillReward,
      addTopDamageReward, removeTopDamageReward,
      save, reload, clearBanner
    }
  }
}
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.85), rgba(0, 0, 0, 0.95));
  background-size: cover;
}

/* Simple fades/scales to match your modal style */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.animate-scaleIn {
  animation: scaleIn .18s ease both;
}
@keyframes scaleIn {
  from { transform: scale(.98); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
